import axios from 'axios';
import { Notification, Message, Loading } from 'element-ui'
import store from '@/store'
import { getToken } from '@/utils/auth'
import errorCode from '@/utils/errorCode'
import { tansParams, blobValidate } from "@/utils/ruoyi"
import cache from '@/plugins/cache'
import { saveAs } from 'file-saver'

let downloadLoadingInstance
// 是否显示重新登录
export let isRelogin = { show: false }

// 登录过期：强制跳转回登录页（全局只执行一次，避免并发401重复触发）。
// 注意：不能走 store.dispatch('LogOut')——它会先请求后端 logout 接口，
// 而 token 已过期时该请求同样返回 401 并挂起，导致跳转永远不会执行。
// 因此用 FedLogOut 仅在本地清掉 token 后整页跳转（history 路由，直接到 /login）。
let isRedirectingToLogin = false
let reloginLoadingInstance = null
function forceRelogin() {
    if (isRedirectingToLogin) return
    isRedirectingToLogin = true
    // 全屏遮罩提示：比右上角 Message 更醒目，同时锁住已失效的页面，
    // 避免用户在跳转完成前继续操作；跳转后随整页刷新自然消失
    reloginLoadingInstance = Loading.service({
        lock: true,
        text: '登录状态已过期，正在跳转到登录页...',
        spinner: 'el-icon-loading',
        // 警告黄风格：浅黄底 + 橙色文字/图标（配色同 el-alert--warning），样式见 element-ui.scss
        background: 'rgba(253, 246, 236, 0.95)',
        customClass: 'relogin-warning-loading'
    })
    const goLogin = () => { location.href = '/login' }
    store.dispatch('FedLogOut').then(goLogin).catch(goLogin)
    // 兜底：极端情况下（如跳转被拦截）10秒后关闭遮罩并允许重新触发，避免页面被永久锁死
    setTimeout(() => {
        if (reloginLoadingInstance) {
            reloginLoadingInstance.close()
            reloginLoadingInstance = null
            isRedirectingToLogin = false
        }
    }, 10 * 1000)
}

// 401（token过期）统一处理：强制跳转登录页，并返回一个永不决议的 pending Promise。
// 整页即将跳走，页面的 then/catch/finally 不会再被触发，
// 因此跳转过程中不会再弹出任何额外报错，挂起的请求随页面刷新一起丢弃。
function reloginPending() {
    forceRelogin()
    return new Promise(() => {})
}

// 解析二进制（blob/arraybuffer）响应下后端返回的 JSON 错误体
function parseBlobJsonBody(res) {
    const data = res.data
    const parse = text => {
        try { return JSON.parse(text) } catch (e) { return null }
    }
    if (typeof Blob !== 'undefined' && data instanceof Blob && typeof data.text === 'function') {
        return data.text().then(parse).catch(() => null)
    }
    if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
        return Promise.resolve(parse(new TextDecoder('utf-8').decode(data)))
    }
    return Promise.resolve(typeof data === 'string' ? parse(data) : (data && typeof data === 'object' ? data : null))
}

/*axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8'*/

// 创建axios实例
const service = axios.create({
    baseURL: process.env.VUE_APP_BASE_API,
    // 超时
    timeout: 3000
})

// request拦截器
service.interceptors.request.use(config => {
    // 关键修复：对于FormData类型（文件上传）不设置Content-Type，由浏览器自动处理
    if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json;charset=utf-8';
    } else {
        // 对于FormData类型，移除可能存在的Content-Type设置，让浏览器自动添加
        delete config.headers['Content-Type'];
    }
    // 是否需要设置 token
    const isToken = (config.headers || {}).isToken === false
    // 是否需要防止数据重复提交
    const isRepeatSubmit = (config.headers || {}).repeatSubmit === false
    if (getToken() && !isToken) {
        config.headers['Authorization'] = 'Bearer ' + getToken() // 让每个请求携带自定义token 请根据实际情况自行修改
    }
    // get请求映射params参数
    if (config.method === 'get' && config.params) {
        let url = config.url + '?' + tansParams(config.params)
        url = url.slice(0, -1)
        config.params = {}
        config.url = url
    }
    if (!isRepeatSubmit && (config.method === 'post' || config.method === 'put')) {
        const requestObj = {
            url: config.url,
            data: typeof config.data === 'object' ? JSON.stringify(config.data) : config.data,
            time: new Date().getTime()
        }
        const requestSize = Object.keys(JSON.stringify(requestObj)).length // 请求数据大小
        const limitSize = 5 * 1024 * 1024 // 限制存放数据5M
        if (requestSize >= limitSize) {
            console.warn(`[${config.url}]: ` + '请求数据大小超出允许的5M限制，无法进行防重复提交验证。')
            return config
        }
        const sessionObj = cache.session.getJSON('sessionObj')
        if (sessionObj === undefined || sessionObj === null || sessionObj === '') {
            cache.session.setJSON('sessionObj', requestObj)
        } else {
            const s_url = sessionObj.url                  // 请求地址
            const s_data = sessionObj.data                // 请求数据
            const s_time = sessionObj.time                // 请求时间
            const interval = 1000                         // 间隔时间(ms)，小于此时间视为重复提交
            if (s_data === requestObj.data && requestObj.time - s_time < interval && s_url === requestObj.url) {
                const message = '数据正在处理，请勿重复提交'
                console.warn(`[${s_url}]: ` + message)
                // 打上防重标记：全局按黄色警告样式提示（而非红色报错），页面 catch 中也可据此跳过自己的错误提示
                const repeatError = new Error(message)
                repeatError.isRepeatSubmit = true
                return Promise.reject(repeatError)
            } else {
                cache.session.setJSON('sessionObj', requestObj)
            }
        }
    }
    return config
}, error => {
    console.log(error)
    Promise.reject(error)
})

// 响应拦截器
service.interceptors.response.use(res => {
        // 二进制数据（文件流）：返回完整响应对象（包含headers）
        if (res.request.responseType === 'blob' || res.request.responseType === 'arraybuffer') {
            // token过期时后端返回的是 JSON 401 错误体：识别后统一走重新登录弹窗，
            // 避免页面把 JSON 当文件保存（损坏文件）或弹出自己的报错；
            // 其余 JSON（如业务错误）仍原样返回给调用方处理
            const contentType = String((res.headers && res.headers['content-type']) || '')
            if (contentType.indexOf('application/json') !== -1) {
                return parseBlobJsonBody(res).then(body => {
                    if (body && Number(body.code) === 401) {
                        return reloginPending()
                    }
                    return res
                })
            }
            return res; // 关键：返回整个响应对象，而不是res.data
        }

        // 新增：读取是否静默错误提示
        const skipErrorMessage = Boolean(res.config && res.config.skipErrorMessage);

        // 未设置状态码则默认成功状态（后端 Result.code 为字符串，统一转数字比较）
        const code = Number(res.data.code || 200)
        // 获取错误信息
        const msg = errorCode[code] || res.data.msg || errorCode['default']
        // 二进制数据则直接返回
        if (res.request.responseType === 'blob'|| res.request.responseType === 'arraybuffer') {
            return res.data
        }
        if (code === 401) {
            // 登录过期：只弹出全局重新登录弹窗，并向调用方返回 pending Promise（不再 reject），
            // 避免页面上的并发请求把错误抛给各自的 catch，导致弹窗旁边又冒出红色报错
            return reloginPending()
        } else if (code === 400) {
            if (!skipErrorMessage) {
                Message({ message: msg, type: 'error' })
            }
            return Promise.reject(new Error(msg))
        } else if (code === 500) {
            if (!skipErrorMessage) {
                Message({ message: msg, type: 'error' })
            }
            return Promise.reject(new Error(msg))
        } else if (code === 450 || code === 451) {
            if (!skipErrorMessage) {
                Message({ message: msg, type: 'error' })
            }
            return Promise.reject(new Error(msg))
        } else if (code === 600) {
            if (!skipErrorMessage) {
                Message({ message: msg, type: 'warning' });
            }
            return res.data;
        } else if (code === 601) {
            if (!skipErrorMessage) {
                Message({ message: msg, type: 'warning' })
            }
            return Promise.reject('error')
        } else if (code === 301) {
            if (!skipErrorMessage) {
                Message({ message: msg, type: 'warning' })
            }
            // 301为“处理中”类警告：把状态码和响应体挂到Error上，
            // 便于调用方识别后自行轮询重试（如物流面单生成中）
            const warnError = new Error(msg)
            warnError.code = code
            warnError.data = res.data
            return Promise.reject(warnError)
        } else if (code !== 200) {
            if (!skipErrorMessage) {
                Notification.error({ title: msg })
            }
            // 需要保留业务错误详情（code/msg）时，将错误对象化交付给调用方处理
            if (res.config && res.config.appErrorData) {
                return Promise.reject({ appError: true, code: res.data.code, msg: res.data.msg || msg, response: res })
            }
            return Promise.reject('error')
        } else {
            return res.data
        }
    },
    error => {
        // HTTP 层 401（网关/鉴权层直接返回 401 状态码的情况）：
        // 同样只弹全局重新登录弹窗，不再向页面抛红色报错
        const httpStatus = error && error.response && error.response.status
        if (httpStatus === 401) {
            const cfg = (error && error.config) || {}
            const isBlobRequest = cfg.responseType === 'blob' || cfg.responseType === 'arraybuffer'
            if (isBlobRequest) {
                // 下载类请求仍需 reject，让调用方（如 download）关闭全屏 loading 做清理
                forceRelogin()
                return Promise.reject(error)
            }
            return reloginPending()
        }

        console.log('err' + error)
        let message = (error && error.message) || '系统接口异常'

        // 新增：读取是否静默错误提示
        const skipErrorMessage = Boolean(
            error &&
            error.config &&
            error.config.skipErrorMessage
        );

        // 防重复提交拦截：属于正常防护行为，用黄色警告轻提示，不按红色报错处理
        if (error && error.isRepeatSubmit) {
            if (!skipErrorMessage) {
                Message({ message: message, type: 'warning', duration: 2 * 1000 })
            }
            return Promise.reject(error)
        }

        if (message == "Network Error") {
            message = "后端接口连接异常"
        } else if (message.includes("timeout")) {
            message = "系统接口请求超时"
        } else if (message.includes("Request failed with status code")) {
            message = "系统接口" + message.substr(message.length - 3) + "异常"
        }

        if (!skipErrorMessage) {
            Message({ message: message, type: 'error', duration: 5 * 1000 })
        }

        return Promise.reject(error)
    }
)

// 通用下载方法
export function download(url, params, filename, config) {
    downloadLoadingInstance = Loading.service({ text: "正在下载数据，请稍候", spinner: "el-icon-loading", background: "rgba(0, 0, 0, 0.7)", })
    return service.post(url, params, {
        transformRequest: [(params) => { return tansParams(params) }],
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        responseType: 'blob',
        ...config
    }).then(async (data) => {
        const isBlob = blobValidate(data)
        if (isBlob) {
            const blob = new Blob([data])
            saveAs(blob, filename)
        } else {
            const resText = await data.text()
            const rspObj = JSON.parse(resText)
            const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode['default']
            Message.error(errMsg)
        }
        downloadLoadingInstance.close()
    }).catch((r) => {
        console.error(r)
        Message.error('下载文件出现错误，请联系管理员！')
        downloadLoadingInstance.close()
    })
}

export default service